import type {
  ITileImageDecodeService,
  TileImageDecodeRequest,
  TileImageDecodeServiceOptions,
  TileImageServiceStatus,
  TileImageWorkerRequest,
  TileImageWorkerResponse,
} from '@/plugins/tiles/mesh/tileImageProtocol'
import { tileImagePeakBytes } from '@/plugins/tiles/mesh/tileImageProtocol'
import { TilesError, throwIfTilesAborted } from '@/plugins/tiles/TilesError'

interface ImageTask {
  readonly id: number
  readonly request: TileImageDecodeRequest
  readonly peakBytes: number
  readonly resolve: (image: ImageBitmap) => void
  readonly reject: (error: unknown) => void
  readonly abort: () => void
}

interface WorkerSlot {
  readonly worker: Worker
  readonly generation: number
  task?: ImageTask
  timer?: ReturnType<typeof setTimeout>
}

function imageError(
  message: string,
  code: 'unsupported-capability' | 'image-worker-error' | 'image-capacity' = 'image-worker-error'
) {
  return new TilesError({ code, stage: 'decode', message, retryable: false })
}

function closeBitmap(bitmap?: ImageBitmap): void {
  try {
    bitmap?.close()
  } catch {
    /* 单项回收异常不能遗留其它 Worker 或预约。 */
  }
}

/** 适配器默认拥有，Record 仅借用；执行容量与缓存预约是同一加工的不同约束。 */
export class TileImageDecodeService implements ITileImageDecodeService {
  private readonly _queue: ImageTask[] = []
  private readonly _workers = new Set<WorkerSlot>()
  private readonly _concurrency: number
  private readonly _maxPeakBytes: number
  private readonly _maxQueued: number
  private readonly _timeoutMs: number
  private readonly _maxRestarts: number
  private _serial = 0
  private _generation = 0
  private _activePeakBytes = 0
  private _failures = 0
  private _closed = false
  private _pumping = false
  private _processingCount = 0
  private _processingTotalMs = 0
  private _processingMaxMs = 0

  constructor(private readonly _options: TileImageDecodeServiceOptions = {}) {
    const integer = (value: number, name: string, min = 1, max = Number.MAX_SAFE_INTEGER) => {
      if (!Number.isSafeInteger(value) || value < min || value > max)
        throw new RangeError(`${name} 必须为 ${min}..${max} 的整数`)
      return value
    }
    this._concurrency = integer(_options.concurrency ?? 2, '图片并发', 1, 16)
    this._maxPeakBytes = integer(_options.maxPeakBytes ?? 1024 ** 3, '图片执行峰值')
    this._maxQueued = integer(_options.maxQueued ?? 256, '图片队列')
    this._timeoutMs = integer(_options.timeoutMs ?? 120000, '图片超时')
    this._maxRestarts = integer(_options.maxRestarts ?? 2, 'Worker 重启次数', 0, 16)
  }

  async decode(request: TileImageDecodeRequest): Promise<ImageBitmap> {
    throwIfTilesAborted(request.signal)
    if (this._closed) throw imageError('图片服务已关闭')
    for (const size of [request.target, request.sourceSize])
      if (![size.width, size.height].every((n) => Number.isSafeInteger(n) && n > 0))
        throw new RangeError('图片尺寸必须为正安全整数')
    if (
      request.target.width > request.sourceSize.width ||
      request.target.height > request.sourceSize.height
    )
      throw new RangeError('图片目标尺寸不能放大原图')
    const peakBytes = tileImagePeakBytes(request)
    if (!Number.isSafeInteger(peakBytes) || peakBytes > this._maxPeakBytes)
      throw imageError('单张图片加工峰值超过执行容量', 'image-capacity')
    if (this._queue.length >= this._maxQueued)
      throw imageError('图片等待队列已满', 'image-capacity')
    return new Promise((resolve, reject) => {
      const task: ImageTask = {
        id: ++this._serial,
        request,
        peakBytes,
        resolve,
        reject,
        abort: () => {
          const index = this._queue.indexOf(task)
          if (index === -1) return // 不可抢占任务保留槽与峰值直到 Worker 回执。
          this._queue.splice(index, 1)
          this.finish(
            task,
            undefined,
            new TilesError({ code: 'cancelled', stage: 'decode', message: '图片已取消' })
          )
          this.pump()
        },
      }
      this._queue.push(task)
      request.signal.addEventListener('abort', task.abort, { once: true })
      if (request.signal.aborted) task.abort()
      else this.pump()
    })
  }

  getStatus(): TileImageServiceStatus {
    return Object.freeze({
      path: 'worker',
      queued: this._queue.length,
      active: [...this._workers].filter((slot) => slot.task).length,
      activePeakBytes: this._activePeakBytes,
      workerCount: this._workers.size,
      failures: this._failures,
      closed: this._closed,
      workerProcessing: this._processingCount
        ? Object.freeze({
            count: this._processingCount,
            totalMs: this._processingTotalMs,
            maxMs: this._processingMaxMs,
          })
        : null,
    })
  }

  dispose(): void {
    if (this._closed) return
    this._closed = true
    const error = imageError('图片服务已销毁')
    for (const task of this._queue.splice(0)) this.finish(task, undefined, error)
    for (const slot of [...this._workers]) this.removeWorker(slot, error)
  }

  private pump(): void {
    if (this._pumping || this._closed) return
    this._pumping = true
    try {
      while (this._queue.length) {
        // FIFO 保留年龄公平性；大项等待所有活动槽结算后必可推进。
        const task = this._queue[0]
        if (task.peakBytes + this._activePeakBytes > this._maxPeakBytes) break
        let slot = [...this._workers].find((item) => !item.task)
        if (!slot) {
          if (this._workers.size >= this._concurrency) break
          if (this._failures > this._maxRestarts) {
            for (const queued of this._queue.splice(0))
              this.finish(queued, undefined, imageError('图片 Worker 重启额度已用尽'))
            break
          }
          try {
            slot = this.createWorker()
          } catch (error) {
            this._failures++
            this._queue.shift()
            this.finish(task, undefined, error)
            continue
          }
        }
        this._queue.shift()
        slot.task = task
        this._activePeakBytes += task.peakBytes
        const active = slot
        slot.timer = setTimeout(() => {
          this._failures++
          this.removeWorker(active, imageError('图片 Worker 超时'))
          this.pump()
        }, this._timeoutMs)
        try {
          const source = task.request.data
          const data = (source instanceof ArrayBuffer ? new Uint8Array(source) : source).slice()
            .buffer
          const message: TileImageWorkerRequest = {
            type: 'decode',
            id: task.id,
            generation: slot.generation,
            data,
            mimeType: task.request.mimeType,
            target: task.request.target,
          }
          slot.worker.postMessage(message, [data])
        } catch (error) {
          this._failures++
          this.removeWorker(slot, imageError(`图片 Worker 转移失败: ${String(error)}`))
        }
      }
    } finally {
      this._pumping = false
    }
  }

  private createWorker(): WorkerSlot {
    if (!this._options.workerFactory && typeof Worker === 'undefined')
      throw imageError('当前环境不支持图片 Worker，请显式选择兼容模式', 'unsupported-capability')
    const relativeWorker = '../../../workers/TileImageDecodeWorker.js'
    const moduleUrl = import.meta.url
    const worker =
      this._options.workerFactory?.() ??
      new Worker(this._options.workerUrl ?? new URL(relativeWorker, moduleUrl), {
        type: 'module',
        name: 'tiles-image',
      })
    const slot: WorkerSlot = { worker, generation: ++this._generation }
    this._workers.add(slot)
    worker.onmessage = (event: MessageEvent<TileImageWorkerResponse>) => {
      const data = event.data
      const task = slot.task
      if (!data || typeof data !== 'object') {
        this._failures++
        this.removeWorker(slot, imageError('图片 Worker 消息无效'))
        this.pump()
        return
      }
      if (!task || data.id !== task.id || data.generation !== slot.generation) {
        if (data.type === 'result') closeBitmap(data.bitmap)
        return
      }
      clearTimeout(slot.timer)
      slot.task = undefined
      this._activePeakBytes -= task.peakBytes
      if (data.type === 'error')
        this.finish(
          task,
          undefined,
          new TilesError({ code: data.code, stage: 'decode', message: data.message })
        )
      else if (data.type === 'result') {
        // 已取消但真实完成的工作仍计入；旧代际或重复回执已在上方排除。
        if (
          this._options.diagnostics &&
          Number.isFinite(data.processingMs) &&
          data.processingMs >= 0
        ) {
          this._processingCount++
          this._processingTotalMs += data.processingMs
          this._processingMaxMs = Math.max(this._processingMaxMs, data.processingMs)
        }
        this.finish(task, data.bitmap)
      } else this.finish(task, undefined, imageError('图片 Worker 消息无效'))
      this.pump()
    }
    const fail = () => {
      this._failures++
      this.removeWorker(slot, imageError('图片 Worker error/messageerror'))
      this.pump()
    }
    worker.onerror = fail
    worker.onmessageerror = fail
    return slot
  }

  private removeWorker(slot: WorkerSlot, error: unknown): void {
    if (!this._workers.delete(slot)) return
    clearTimeout(slot.timer)
    // 保留 onmessage 清理通道，已排入事件队列的位图仍会被关闭。
    slot.worker.onerror = null
    slot.worker.onmessageerror = null
    try {
      slot.worker.terminate()
    } catch {
      /* 继续结算当前任务。 */
    }
    const task = slot.task
    slot.task = undefined
    if (task) {
      this._activePeakBytes -= task.peakBytes
      this.finish(task, undefined, error)
    }
  }

  private finish(task: ImageTask, bitmap?: ImageBitmap, error?: unknown): void {
    task.request.signal.removeEventListener('abort', task.abort)
    if (task.request.signal.aborted || this._closed) {
      closeBitmap(bitmap)
      task.reject(
        error ?? new TilesError({ code: 'cancelled', stage: 'decode', message: '图片已取消' })
      )
    } else if (error) task.reject(error)
    else if (bitmap) {
      if (
        bitmap.width !== task.request.target.width ||
        bitmap.height !== task.request.target.height
      ) {
        closeBitmap(bitmap)
        task.reject(imageError('图片 Worker 返回超尺寸结果'))
      } else task.resolve(bitmap)
    } else task.reject(imageError('图片 Worker 未返回位图'))
  }
}
